import type { CoherenceDiagnostic } from '@/types/api'
import { getCoherenceColor, getCoherenceFlagText } from '@/lib/chart-transformers'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Info, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'

interface CoherenceBadgeProps {
  diagnostic: CoherenceDiagnostic
}

export function CoherenceBadge({ diagnostic }: CoherenceBadgeProps) {
  const { coherence_score, mahalanobis_distance, flag_text, calculation_status, nearest_historical_analog } = diagnostic

  const color = getCoherenceColor(coherence_score)
  const flagText = getCoherenceFlagText(coherence_score, flag_text)

  // Determine status icon
  const StatusIcon = () => {
    if (coherence_score === null) {
      return <XCircle className="h-5 w-5 text-gray-400" />
    }
    if (coherence_score >= 80) {
      return <CheckCircle className="h-5 w-5 text-emerald-500" />
    }
    if (coherence_score >= 50) {
      return <AlertTriangle className="h-5 w-5 text-amber-500" />
    }
    return <AlertTriangle className="h-5 w-5 text-red-500" />
  }

  // Score display
  const ScoreDisplay = () => {
    if (coherence_score === null) {
      return (
        <div className="text-center">
          <span className="text-3xl font-bold text-gray-400">N/A</span>
          <p className="text-xs text-gray-500 mt-1">Computation failed</p>
        </div>
      )
    }

    return (
      <div className="text-center">
        <span
          className="text-4xl font-bold"
          style={{ color }}
        >
          {coherence_score}
        </span>
        <span className="text-lg text-gray-500">/100</span>
        <p className="text-xs text-gray-500 mt-1">Coherence Score</p>
      </div>
    )
  }

  // Tooltip content explaining coherence
  const CoherenceExplanation = () => (
    <div className="space-y-2 max-w-xs">
      <p className="font-semibold">What is Coherence?</p>
      <p className="text-sm">
        The coherence score measures how plausible your scenario is compared to historical market behavior.
      </p>
      <ul className="text-sm space-y-1 text-gray-600">
        <li>• <strong>80-100:</strong> High plausibility - scenario aligns well with historical patterns</li>
        <li>• <strong>50-79:</strong> Moderate plausibility - some divergence from historical norms</li>
        <li>• <strong>0-49:</strong> Low plausibility - significant departure from historical behavior</li>
      </ul>
      {mahalanobis_distance !== null && (
        <p className="text-sm text-gray-600">
          Mahalanobis distance: {mahalanobis_distance.toFixed(2)}
        </p>
      )}
    </div>
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            Coherence Score
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right">
                  <CoherenceExplanation />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <StatusIcon />
        </div>
        <CardDescription>
          Scenario plausibility assessment
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Score Display */}
        <div className="flex flex-col items-center justify-center py-4 bg-gray-50 rounded-lg">
          <ScoreDisplay />
        </div>

        {/* Flag Text */}
        <div className="text-center">
          <p className={`text-sm font-medium ${coherence_score !== null && coherence_score < 50 ? 'text-red-600' : 'text-gray-700'}`}>
            {flagText}
          </p>
        </div>

        {/* Calculation Status */}
        <div className="flex items-center justify-center gap-2">
          <Badge
            variant={calculation_status === 'success' ? 'outline' : 'secondary'}
            className={`
              ${calculation_status === 'success' ? 'text-emerald-600 border-emerald-200' : ''}
              ${calculation_status === 'insufficient_data' ? 'text-amber-600' : ''}
              ${calculation_status === 'computation_error' ? 'text-red-600' : ''}
            `}
          >
            {calculation_status === 'success' && '✓ Computed'}
            {calculation_status === 'insufficient_data' && '⚠ Insufficient Data'}
            {calculation_status === 'computation_error' && '✗ Computation Error'}
          </Badge>
        </div>

        {/* Nearest Historical Analog */}
        {nearest_historical_analog && (
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Most Similar Historical Episode:</p>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-blue-600">
                {nearest_historical_analog.episode_name}
              </Badge>
              <span className="text-xs text-gray-500">
                ({nearest_historical_analog.similarity_score.toFixed(1)}% similarity)
              </span>
            </div>
          </div>
        )}

        {/* Interpretation Guide */}
        {coherence_score !== null && coherence_score < 50 && (
          <div className="bg-red-50 rounded-lg p-3 text-sm">
            <p className="font-medium text-red-800 mb-1">Low Coherence Alert</p>
            <p className="text-red-700 text-xs">
              Your scenario significantly departs from historical market behavior.
              Consider reviewing your assumptions or shock magnitudes.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface EmptyCoherenceBadgeProps {
  reason?: 'no-data' | 'loading'
}

export function EmptyCoherenceBadge({ reason = 'no-data' }: EmptyCoherenceBadgeProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Coherence Score</CardTitle>
        <CardDescription>Scenario plausibility assessment</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg">
          <Info className="h-10 w-10 text-gray-300 mb-2" />
          <p className="text-gray-500">
            {reason === 'loading' ? 'Computing coherence...' : 'No coherence data available'}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
